/**
 * Chrome extension side panel: the stored session belongs to another firm than the Gmail
 * account next to the panel (for example signed in to the host before the domain was mapped).
 * Same card look as the panel's sign-in screen.
 */
import { useTranslation } from '@/i18n';

interface ExtensionFirmMismatchProps {
  firmName: string;
  signedInFirm: string;
  signedInUser: string;
  onSwitch: () => void;
  onContinue: () => void;
}

export const ExtensionFirmMismatch = ({ firmName, signedInFirm, signedInUser, onSwitch, onContinue }: ExtensionFirmMismatchProps) => {
  const { t } = useTranslation();
  return (
  <div
    style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
      backgroundColor: '#f5f7fa', fontFamily: "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: '#0f172a',
    }}
  >
    <div
      role="alert"
      style={{
        width: '100%', maxWidth: '400px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(15, 23, 42, .06), 0 1px 2px rgba(15, 23, 42, .04)', padding: '24px 20px 22px', textAlign: 'center',
      }}
    >
      <div
        aria-hidden="true"
        style={{ width: 52, height: 52, margin: '0 auto', borderRadius: '50%', display: 'grid', placeItems: 'center', background: '#fffbeb', color: '#b45309' }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        </svg>
      </div>
      <h1 style={{ fontSize: '16px', fontWeight: 700, margin: '12px 0 0' }}>{t('Auth:SignedInToDifferentFirm', { defaultValue: 'Signed in to a different firm' })}</h1>
      <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
        {t('Auth:GmailAccountBelongsTo', { defaultValue: 'This Gmail account belongs to' })} <strong style={{ color: '#0f172a' }}>{firmName}</strong>{t('Auth:ButPanelSignedInTo', { defaultValue: ', but the panel is signed in to' })}{' '}
        <strong style={{ color: '#0f172a' }}>{signedInFirm}</strong> {t('Auth:AsUser', { defaultValue: 'as {{user}}.', user: signedInUser })}
      </p>
      <button
        type="button"
        onClick={onSwitch}
        style={{
          width: '100%', marginTop: '18px', padding: '11px 16px', border: 'none', borderRadius: '8px',
          background: '#1e3a5f', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
        }}
      >
        {t('Auth:SignInToFirm', { defaultValue: 'Sign in to {{firm}}', firm: firmName })}
      </button>
      <button
        type="button"
        onClick={onContinue}
        style={{
          width: '100%', marginTop: '8px', padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px',
          background: 'transparent', color: '#1e3a5f', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
        }}
      >
        {t('Auth:ContinueAsUser', { defaultValue: 'Continue as {{user}}', user: signedInUser })}
      </button>
    </div>
  </div>
  );
};
